import React from 'react';
import Config from '../../../../config';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import Avatar from '@material-ui/core/Avatar';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import Button from '@material-ui/core/Button';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import { Box } from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => {
  return {
    panelSummaryContent: {
      margin: `${theme.spacing(2.5)}px 0`
    },
    detailsContainer: {
      paddingTop: 0
    },
    stepsContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(7.5),
      paddingRight: theme.spacing(7.5)
    },
    subHeader: {
      color: theme.palette.warning.main,
      fontWeight: 500
    },
    avatar: {
      ...theme.typography.h3,
      height: 48,
      width: 48,
      color: theme.palette.secondary.main,
      backgroundColor: '#F7FBFF',
      alignItems: 'center',
      margin: 'auto',
      marginRight: theme.spacing(2)
    },
    imagePanel: {
      display: 'flex'
    },
    imageContainer: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      height: 96,
      width: 96,
      display: 'flex',
      marginRight: theme.spacing(3)
    },
    documentContainer: {
      paddingTop: 0,
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      display: 'flex',
      flexDirection: 'column'
    },
    documentHeader: {
      margin: 'auto',
      paddingBottom: theme.spacing(1)
    },
    videoWrapper: {
      width: '100%',
      maxWidth: '650px',
      maxHeight: 'auto',
      margin: '0 auto'
    },
    videoContainer: {
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      paddingTop: '56.25%'
    },
    videoiFrame: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%'
    },
    importLogo: {
      height: 46,
      width: 46,
      margin: 'auto',
      color: theme.palette.secondary.main
    },
    textUnderline: {
      textDecoration: 'underline'
    },
    sections: {
      marginLeft: theme.spacing(3)
    },
    assignButton: {
      background: '#FFFFFF',
      border: '1px solid #969696',
      color: '#2D2D2D',
      '&:hover': {
        background: '#FFFFFF'
      }
    }
  };
});

const ManualEntryPanel: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('ManualEntryPanel');
  const createLicenseEducatorsVideo = `${Config.s3Url}/support/video/PLI-Creating_Licensing_Educators.mp4`;
  const createLicenseStudentsVideo = `${Config.s3Url}/support/video/PLI-Creating_Licensing_Students.mp4`;

  return (
    <Grid item key={'manual-entry'}>
      <ExpansionPanel>
        <ExpansionPanelSummary
          aria-controls="manual-entry-content"
          id="manual-entry-content"
          classes={{ content: classes.panelSummaryContent }}
          expandIcon={<ExpandMoreIcon />}
        >
          <Avatar className={classes.avatar}>3</Avatar>
          <Grid container spacing={0} alignItems="center" justify="space-between">
            <Grid item>
              <Typography variant="h3">{t('Manually Enter User Information')}</Typography>
              <Typography variant="subtitle1">
                <span>
                  {t('Step by step instructions for assigning licenses to students or educators.')}
                </span>
                <span className={classes.subHeader}>
                  {t(' (Not recommended for districts and schools.)')}
                </span>
              </Typography>
            </Grid>
            <Grid item className={classes.imagePanel}>
              <Paper className={classes.imageContainer} elevation={0}>
                <InsertDriveFileIcon className={classes.importLogo} />
              </Paper>
            </Grid>
          </Grid>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <Grid container spacing={0} direction="column">
            <Grid item>
              <Divider />
            </Grid>
            <Grid item>
              <Grid container spacing={3} className={classes.stepsContainer}>
                <Grid item xs={12}>
                  <ExpansionPanel elevation={3}>
                    <ExpansionPanelSummary
                      aria-controls="create-license-educators"
                      id="create-license-educators"
                      classes={{ content: classes.panelSummaryContent }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Typography variant="h4">
                        <span>{t('Creating and Licensing Educators')}</span>
                      </Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails className={classes.detailsContainer}>
                      <Grid container spacing={0} direction="column">
                        <Grid item>
                          <Divider />
                        </Grid>
                        <Grid item>
                          <Grid container spacing={3} className={classes.stepsContainer}>
                            <Grid item xs={12}>
                              <Paper className={classes.documentContainer} elevation={0}>
                                <Box className={classes.videoWrapper}>
                                  <Box className={classes.videoContainer}>
                                    <video
                                      id="video-iframe"
                                      title={t('Assign Licenses to Users Video')}
                                      className={classes.videoiFrame}
                                      src={createLicenseEducatorsVideo}
                                      controls
                                    ></video>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="h3" className={classes.textUnderline}>
                                {t('To create and assign licenses to NEW educators')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 1')}</Typography>
                              <Typography variant="subtitle1">
                                {t(
                                  'From the Admin Console, select the User Management tile and click on the '
                                )}
                                <Button
                                  aria-label="Invite User"
                                  variant="contained"
                                  color="primary"
                                  size="large"
                                  disableElevation
                                  startIcon={<AddCircleIcon />}
                                >
                                  {t('Invite User')}
                                </Button>
                                {t(' button in the top right navigation.')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 2')}</Typography>
                              <Typography variant="subtitle1">
                                {t('Fill in the appropriate information:')}
                              </Typography>
                              <Typography variant="subtitle1">
                                <ol>
                                  <li>{t('Input name and email address of educator.')}</li>
                                  <li>
                                    {t('Choose organization and role and select “Add Role”.')}
                                  </li>
                                  <li>
                                    {t(
                                      'Assign product(s) by using the drop down menu and select “Assign Product.”'
                                    )}
                                  </li>
                                </ol>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 3')}</Typography>
                              <Typography variant="subtitle1">{t('Click on Invite.')}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="h3" className={classes.textUnderline}>
                                {t(
                                  'To assign licenses to educators ALREADY LISTED in your User Management tile'
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 1')}</Typography>
                              <Typography variant="subtitle1">
                                {t(
                                  'From the Admin Console, select the User Management tile and locate the educator you would like to assign a license. Click on the 3 dots in the top right of their named tile and select Manage User Products.'
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 2')}</Typography>
                              <Typography variant="subtitle1">
                                {t('Find the license you would like to assign and select the ')}
                                <Button
                                  aria-label="Assign"
                                  variant="contained"
                                  color="secondary"
                                  size="small"
                                  className={classes.assignButton}
                                  disableElevation
                                >
                                  {t('Assign')}
                                </Button>
                                {t(' button.')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 3')}</Typography>
                              <Typography variant="subtitle1">
                                {t('Click on Save & Close.')}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </ExpansionPanelDetails>
                  </ExpansionPanel>

                  <ExpansionPanel elevation={3}>
                    <ExpansionPanelSummary
                      aria-controls="create-license-students"
                      id="create-license-students"
                      classes={{ content: classes.panelSummaryContent }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Typography variant="h4">
                        <span>{t('Creating and Licensing Students')}</span>
                      </Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails className={classes.detailsContainer}>
                      <Grid container spacing={0} direction="column">
                        <Grid item>
                          <Divider />
                        </Grid>
                        <Grid item>
                          <Grid container spacing={3} className={classes.stepsContainer}>
                            <Grid item xs={12}>
                              <Paper className={classes.documentContainer} elevation={0}>
                                <Box className={classes.videoWrapper}>
                                  <Box className={classes.videoContainer}>
                                    <video
                                      id="video-iframe"
                                      title={t('Assign Licenses to Users Video')}
                                      className={classes.videoiFrame}
                                      src={createLicenseStudentsVideo}
                                      controls
                                    ></video>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="h4" className={classes.textUnderline}>
                                {t(
                                  'To Create Classes and License Students when YOU are the teacher'
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 1')}</Typography>
                              <Typography variant="subtitle1">
                                {t('Go to My Classes & Students in the left-hand navigation.')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 2')}</Typography>
                              <Typography variant="subtitle1">
                                {t('Click on the ')}
                                <Button
                                  aria-label="Add Button"
                                  variant="contained"
                                  color="primary"
                                  size="large"
                                  disableElevation
                                  startIcon={<AddCircleIcon />}
                                >
                                  {t('Create Class')}
                                </Button>
                                {t(' button in the top right-hand corner.')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 3')}</Typography>
                              <Typography variant="subtitle1">
                                {t(
                                  'Go through steps 1-4 in the "Create Class" window, filling in the necessary Class Details:'
                                )}
                              </Typography>
                              <Typography variant="subtitle1">
                                <ol>
                                  <li>{t('Create Class')}</li>
                                  <li>{t('Add Students')}</li>
                                  <li>{t('Assign Products')}</li>
                                  <li>{t('Review & Confirm')}</li>
                                </ol>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 4')}</Typography>
                              <Typography variant="subtitle1">
                                {t('Click on Confirm & Create Class.')}
                              </Typography>
                            </Grid>

                            <Grid item xs={12}>
                              <Typography variant="h4" className={classes.textUnderline}>
                                {t(
                                  'To Create Classes and License Students when YOU are the digital administrator'
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 1')}</Typography>
                              <Typography variant="subtitle1">
                                <strong>{t('NOTE: ')}</strong>
                                {t(
                                  'If educators are not already existing in the system, complete that step first.'
                                )}
                              </Typography>
                              <Typography variant="subtitle1">
                                {t(
                                  'Go to the Admin Console and select the Classes & Students tile.'
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 2')}</Typography>
                              <Typography variant="subtitle1">
                                {t('Click on the ')}
                                <Button
                                  aria-label="Add Button"
                                  variant="contained"
                                  color="primary"
                                  size="large"
                                  disableElevation
                                  startIcon={<AddCircleIcon />}
                                >
                                  {t('Create Class')}
                                </Button>
                                {t(' button in the top right-hand corner.')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 3')}</Typography>
                              <Typography variant="subtitle1">
                                {t(
                                  'Go through steps 1-4 in the "Create Class" window, filling in the necessary Class Details:'
                                )}
                              </Typography>
                              <Typography variant="subtitle1">
                                <ol>
                                  <li>{t('Create Class')}</li>
                                  <li>{t('Add Students')}</li>
                                  <li>{t('Assign Products')}</li>
                                  <li>{t('Review & Confirm')}</li>
                                </ol>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.sections}>
                              <Typography variant="h3">{t('Step 4')}</Typography>
                              <Typography variant="subtitle1">
                                {t('Click on Confirm & Create Class.')}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </ExpansionPanelDetails>
                  </ExpansionPanel>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </Grid>
  );
};

export default ManualEntryPanel;
